import { UserType } from '@/data/constants';

/**
 * Single source of truth for what each account type is allowed to see and do.
 * Imported by the router guards, the header navigation and every dashboard so
 * a role can never "leak" contractor tooling into a homeowner account.
 */

export type Role = UserType;

/**
 * COVER IMAGES moved to src/lib/coverImage.ts (upload -> trade library ->
 * branded marketplace cover). Re-exported here so the existing call sites
 * (JobCard, OwnerDashboard, AdminModeration) keep one import path and there is
 * still exactly ONE implementation.
 */
export { coverImageFor, coverUrlFor, PROJECT_PLACEHOLDER, coverCoverageReport } from '@/lib/coverImage';
export type { ProjectCover, CoverSource } from '@/lib/coverImage';


export const isHomeowner = (role?: string | null) => role === 'homeowner';
export const isGc = (role?: string | null) => role === 'gc';
export const isSub = (role?: string | null) => role === 'subcontractor';

/** Only contractor-side accounts may buy / unlock leads or hold a membership. */
export const canBuyLeads = (role?: string | null) => role === 'subcontractor' || role === 'gc';
export const canHoldSubscription = (role?: string | null) => canBuyLeads(role);

/** Homeowners post projects; GCs post work orders; subs do neither. */
export const canPostProjects = (role?: string | null) => role === 'homeowner';
export const canPostWorkOrders = (role?: string | null) => role === 'gc';

/** Who is allowed to browse the lead marketplace feed. */
export const canBrowseMarketplace = (role?: string | null) => canBuyLeads(role);

export interface RoleCapabilities {
  postProjects: boolean;
  postWorkOrders: boolean;
  browseMarketplace: boolean;
  buyLeads: boolean;
  subscriptions: boolean;
  leadBalance: boolean;
}

export const capabilitiesFor = (role?: string | null): RoleCapabilities => ({
  postProjects: canPostProjects(role),
  postWorkOrders: canPostWorkOrders(role),
  browseMarketplace: canBrowseMarketplace(role),
  buyLeads: canBuyLeads(role),
  subscriptions: canHoldSubscription(role),
  leadBalance: canBuyLeads(role),
});

/** Normalises anything stored in auth metadata / the profile row to a valid role. */
export const normalizeRole = (value?: string | null): Role => {
  const v = (value || '').toLowerCase();
  if (v === 'homeowner' || v === 'owner' || v === 'home_owner') return 'homeowner';
  if (v === 'gc' || v === 'general_contractor' || v === 'contractor') return 'gc';
  if (v === 'subcontractor' || v === 'sub') return 'subcontractor';
  return 'homeowner';
};
